import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { STATUS_LABELS, STATUS_CLASSES } from "@/lib/constants";
import { ChevronLeft, ChevronRight, CalendarClock, AlertTriangle, Clock } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function Pianificazione() {
  const [assemblies, setAssemblies] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => { api.get("/assemblies").then((r) => setAssemblies(r.data)); }, []);

  const active = useMemo(
    () => assemblies.filter((a) => a.due_date && ["in_attesa", "in_corso"].includes(a.status)),
    [assemblies]
  );

  const byDate = useMemo(() => {
    const m = {};
    active.forEach((a) => { (m[a.due_date] = m[a.due_date] || []).push(a); });
    return m;
  }, [active]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const upcoming = useMemo(() => [...active].sort((x, y) => x.due_date.localeCompare(y.due_date)), [active]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const startOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const dateStr = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Pianificazione Commesse</h1>
        <p className="text-slate-500 text-sm mt-1">Date previste e scadenze delle commesse in lavorazione.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-amber-400" /> {MONTHS[month]} {year}
            </h2>
            <div className="flex gap-1">
              <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"><ChevronLeft className="h-4 w-4" /></button>
              <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {DOW.map((d) => <div key={d} className="text-center text-[11px] font-mono-data uppercase tracking-wider text-slate-500 py-1">{d}</div>)}
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const ds = dateStr(d);
              const items = byDate[ds] || [];
              const isToday = ds === todayStr;
              const overdue = ds < todayStr && items.length > 0;
              return (
                <div key={i} className={`min-h-[64px] rounded-lg border p-1.5 text-left ${isToday ? "border-amber-500/60 bg-amber-500/5" : "border-slate-800 bg-slate-950/40"}`}>
                  <div className={`text-xs font-mono-data ${isToday ? "text-amber-400" : "text-slate-500"}`}>{d}</div>
                  {items.slice(0, 2).map((it) => (
                    <div key={it.id} className={`mt-1 truncate text-[10px] px-1 py-0.5 rounded ${overdue ? "bg-rose-500/15 text-rose-300" : "bg-blue-500/15 text-blue-300"}`} title={`${it.commessa} · ${it.part_name}`}>
                      {it.commessa}
                    </div>
                  ))}
                  {items.length > 2 && <div className="text-[10px] text-slate-500 mt-0.5">+{items.length - 2}</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming list */}
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
          <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><Clock className="h-4 w-4 text-amber-400" /> Prossime scadenze</h2>
          <div className="space-y-3">
            {upcoming.length === 0 && <p className="text-slate-500 text-sm">Nessuna scadenza pianificata.</p>}
            {upcoming.map((a) => {
              const overdue = a.due_date < todayStr;
              return (
                <div key={a.id} data-testid={`planning-item-${a.id}`} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono-data text-sm text-amber-400">{a.commessa}</span>
                    <span className={`text-[11px] font-mono-data flex items-center gap-1 ${overdue ? "text-rose-400" : "text-slate-400"}`}>
                      {overdue && <AlertTriangle className="h-3 w-3" />}{a.due_date}
                    </span>
                  </div>
                  <div className="text-sm text-slate-200 mt-1">{a.part_name}</div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-slate-500">{a.line_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_CLASSES[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
